import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { RefreshCw, Sparkles, Pin, EyeOff, AlertTriangle, DollarSign, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const Btn = ({ children, onClick, testid, busy, variant = "dark" }) => (
  <button data-testid={testid} onClick={onClick} disabled={busy}
    className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2 transition-colors disabled:opacity-50 ${variant === "gold" ? "bg-taven-gold text-black hover:bg-taven-black hover:text-taven-gold" : "bg-taven-black text-white hover:bg-taven-gold hover:text-black"}`}>
    {children}
  </button>
);

export default function AdminCatalog() {
  const [settings, setSettings] = useState(null);
  const [state, setState] = useState({});
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [priceForm, setPriceForm] = useState({ handle: "", cost: "", shipping: "", market_reference: "", market_source: "amazon" });
  const [priceResult, setPriceResult] = useState(null);

  const load = async () => {
    try {
      const s = await adminClient().get("/admin/catalog/settings");
      setSettings(s.data.settings); setState(s.data.state || {});
      const r = await adminClient().get("/admin/catalog/review");
      setReview(r.data);
    } catch { toast.error("Failed to load catalog"); }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async (patch) => {
    try {
      const { data } = await adminClient().put("/admin/catalog/settings", patch);
      setSettings(data.settings); toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await adminClient().post("/admin/catalog/refresh");
      toast.success(`Synced ${data.sync.synced} · mapped ${data.sync.auto_mapped} · review ${data.sync.review_needed}`);
      await load();
    } catch { toast.error("Refresh failed"); } finally { setBusy(false); }
  };

  const computePrice = async () => {
    if (!priceForm.handle || !priceForm.cost) { toast.error("Enter product handle and cost"); return; }
    setBusy(true);
    try {
      const body = { cost: parseFloat(priceForm.cost), shipping: parseFloat(priceForm.shipping || 0),
        market_source: priceForm.market_source };
      if (priceForm.market_reference) body.market_reference = parseFloat(priceForm.market_reference);
      const { data } = await adminClient().put(`/admin/catalog/pricing/${priceForm.handle}`, body);
      setPriceResult(data.pricing);
    } catch { toast.error("Pricing failed"); } finally { setBusy(false); }
  };

  const seoRewrite = async (handle) => {
    setBusy(true);
    try {
      const { data } = await adminClient().post(`/admin/catalog/seo/${handle}`);
      toast.success(`SEO rewritten: ${data.seo_title}`);
    } catch { toast.error("AI rewrite failed"); } finally { setBusy(false); }
  };

  const toggle = async (kind, handle) => {
    try { await adminClient().post(`/admin/catalog/${kind}/${handle}`); toast.success(`${kind} toggled`); }
    catch { toast.error("Action failed"); }
  };
  const approve = async (handle) => {
    try { await adminClient().post(`/admin/catalog/approve/${handle}`); toast.success("Approved"); await load(); }
    catch { toast.error("Approve failed"); }
  };

  if (!settings) return <div>Loading catalog…</div>;

  return (
    <div className="space-y-6" data-testid="admin-catalog">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-black font-display">Catalog & Rotation</h2>
          <p className="text-sm text-muted-foreground">Auto-sync, admin-controlled rotation, categorization & pricing. Read-only to the storefront.</p>
        </div>
        <Btn testid="catalog-refresh" onClick={refresh} busy={busy} variant="gold"><RefreshCw className="w-4 h-4" /> Manual Refresh</Btn>
      </div>

      {/* Settings */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="border border-black/10 bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Rotation Interval</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {[{ v: 0.5, l: "30m" }, { v: 1, l: "1h" }, { v: 3, l: "3h" }, { v: 6, l: "6h" }, { v: 12, l: "12h" }, { v: 24, l: "24h" }].map((o) => (
              <button key={o.v} data-testid={`catalog-interval-${o.v}`} onClick={() => saveSettings({ rotation_interval_hours: o.v })}
                className={`text-sm font-bold px-3 py-1.5 ${Number(settings.rotation_interval_hours) === o.v ? "bg-taven-gold text-black" : "bg-taven-gray"}`}>{o.l}</button>
            ))}
          </div>
        </div>
        <div className="border border-black/10 bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Automatic Rotation</p>
          <button data-testid="catalog-rotation-enabled" onClick={() => saveSettings({ rotation_enabled: !settings.rotation_enabled })}
            className={`mt-2 text-sm font-bold px-3 py-1.5 ${settings.rotation_enabled ? "bg-green-600 text-white" : "bg-taven-gray"}`}>{settings.rotation_enabled ? "ENABLED" : "DISABLED"}</button>
        </div>
        <div className="border border-black/10 bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Rotation Mode</p>
          <select data-testid="catalog-rotation-mode" value={settings.rotation_mode || "cycle"} onChange={(e) => saveSettings({ rotation_mode: e.target.value })}
            className="w-full mt-2 bg-taven-gray px-3 py-1.5 text-sm">
            <option value="cycle">Cycle (fair rotation)</option>
            <option value="random">Random</option>
            <option value="newest">New Arrivals</option>
            <option value="bestseller">Best Sellers</option>
          </select>
        </div>
        <div className="border border-black/10 bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Products / Collection</p>
          <input type="number" min="1" max="24" defaultValue={settings.products_per_collection} data-testid="catalog-per-collection"
            onBlur={(e) => saveSettings({ products_per_collection: parseInt(e.target.value) })}
            className="w-full mt-2 bg-taven-gray px-3 py-1.5 text-sm" />
        </div>
        <div className="border border-black/10 bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Auto-publish clean</p>
          <button data-testid="catalog-auto-publish" onClick={() => saveSettings({ auto_publish: !settings.auto_publish })}
            className={`mt-2 text-sm font-bold px-3 py-1.5 ${settings.auto_publish ? "bg-green-600 text-white" : "bg-taven-gray"}`}>{settings.auto_publish ? "ON" : "OFF"}</button>
        </div>
        <div className="border border-black/10 bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Last Sync</p>
          <p className="text-xs mt-2">{state.last_sync ? new Date(state.last_sync).toLocaleString() : "—"}</p>
          <p className="text-[11px] text-muted-foreground mt-1">Mapped {state.auto_mapped ?? 0} · Uncat {state.uncategorized ?? 0}</p>
        </div>
      </div>

      {/* Price engine */}
      <div className="border border-black/10 bg-white p-5">
        <h3 className="font-black font-display flex items-center gap-2"><DollarSign className="w-4 h-4 text-taven-gold" /> Profit-Protection Price Engine</h3>
        <p className="text-xs text-muted-foreground mt-1">Enter cost + optional market reference (Amazon/Walmart/Google). 10%-below-market applies only when profitable.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2 mt-3">
          <input placeholder="product handle" data-testid="price-handle" value={priceForm.handle} onChange={(e) => setPriceForm({ ...priceForm, handle: e.target.value })} className="bg-taven-gray px-3 py-2 text-sm lg:col-span-2" />
          <input placeholder="cost $" type="number" data-testid="price-cost" value={priceForm.cost} onChange={(e) => setPriceForm({ ...priceForm, cost: e.target.value })} className="bg-taven-gray px-3 py-2 text-sm" />
          <input placeholder="shipping $" type="number" value={priceForm.shipping} onChange={(e) => setPriceForm({ ...priceForm, shipping: e.target.value })} className="bg-taven-gray px-3 py-2 text-sm" />
          <input placeholder="market ref $ (optional)" type="number" data-testid="price-market" value={priceForm.market_reference} onChange={(e) => setPriceForm({ ...priceForm, market_reference: e.target.value })} className="bg-taven-gray px-3 py-2 text-sm" />
        </div>
        <div className="mt-3"><Btn testid="price-compute" onClick={computePrice} busy={busy}>Compute Safe Price</Btn></div>
        {priceResult && (
          <div className={`mt-3 p-3 text-sm border ${priceResult.status === "ok" ? "border-green-500 bg-green-50" : priceResult.status === "warning" ? "border-orange-500 bg-orange-50" : "border-yellow-500 bg-yellow-50"}`} data-testid="price-result">
            <p className="font-bold">Recommended: ${priceResult.recommended_price} · Min safe: ${priceResult.min_safe_price} · Landed: ${priceResult.landed}</p>
            <p className="text-xs mt-1 flex items-center gap-1">{priceResult.status !== "ok" && <AlertTriangle className="w-3 h-3" />} {priceResult.note}</p>
          </div>
        )}
      </div>

      {/* Review lists */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border border-black/10 bg-white p-5">
          <h3 className="font-black font-display flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-orange-500" /> Product Review Needed ({review?.review_count ?? 0})</h3>
          <div className="mt-3 space-y-2 max-h-72 overflow-y-auto" data-testid="catalog-review-list">
            {(review?.product_review_needed || []).slice(0, 30).map((p) => (
              <div key={p.handle} className="flex items-center gap-2 text-xs border-b border-black/5 pb-2">
                {p.image && <img src={p.image} alt="" className="w-8 h-8 object-cover" />}
                <div className="flex-1 min-w-0"><p className="truncate font-medium">{p.title}</p><p className="text-[10px] text-orange-600">{[...(p.quality_flags || []), ...(p.content_flags || [])].join(", ")}</p></div>
                <button onClick={() => seoRewrite(p.handle)} title="AI SEO rewrite" className="text-taven-gold"><Sparkles className="w-4 h-4" /></button>
                <button onClick={() => approve(p.handle)} title="Approve" className="text-green-600"><CheckCircle2 className="w-4 h-4" /></button>
              </div>
            ))}
            {(review?.review_count ?? 0) === 0 && <p className="text-xs text-muted-foreground">No products need review. 🎉</p>}
          </div>
        </div>
        <div className="border border-black/10 bg-white p-5">
          <h3 className="font-black font-display">Uncategorized Review ({review?.uncategorized_count ?? 0})</h3>
          <div className="mt-3 space-y-2 max-h-72 overflow-y-auto" data-testid="catalog-uncategorized-list">
            {(review?.uncategorized_review || []).slice(0, 30).map((p) => (
              <div key={p.handle} className="flex items-center gap-2 text-xs border-b border-black/5 pb-2">
                {p.image && <img src={p.image} alt="" className="w-8 h-8 object-cover" />}
                <p className="flex-1 min-w-0 truncate font-medium">{p.title}</p>
                <button onClick={() => toggle("pin", p.handle)} title="Pin"><Pin className="w-4 h-4" /></button>
                <button onClick={() => toggle("exclude", p.handle)} title="Exclude"><EyeOff className="w-4 h-4" /></button>
                <button onClick={() => seoRewrite(p.handle)} title="AI SEO rewrite" className="text-taven-gold"><Sparkles className="w-4 h-4" /></button>
              </div>
            ))}
            {(review?.uncategorized_count ?? 0) === 0 && <p className="text-xs text-muted-foreground">All products categorized.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
